import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import {
  Delete,
  Edit,
  AutoAwesomeSharp,
  ArrowBack,
  KeyboardArrowDownOutlined,
  CheckCircle,
  Done,
} from '@mui/icons-material';

import Loading from 'components/Loading';
import styled from 'styled-components';
import { Button, Col, Input, Modal, Row, message, Card, Tabs } from 'antd';
import { DeleteModal } from 'components/Dialog';
import { PlusOutlined } from '@ant-design/icons';
import { filter, get, isEmpty, groupBy, find } from 'lodash';
import { handleDeleteStanders, handleApproveStandard } from 'store/story/thunks';
import { useStandards } from 'store/story/selectors';
import {
  getStandardDescription,
  getStandardAbbr,
  normalizeGradeLevel,
  compareGradeLevels,
  getGradeGroupingKey,
  normalizeStateForComparison,
  statesMatch,
  getStateNameFromCode,
} from 'utils/lib/standardHelper';
import { useOrgStandardCodes } from 'store/organizations/selectors';
import { useHasPermission } from 'utils/permissions';
import StandardForm from './StandardForm';
import { STANDARD_GRADE_LEVEL } from 'utils/config/reference';
import AiGenerateStandardsModal from 'components/AdminStandards/AiGenerateStandardsModal';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

const StableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StableWrapperMain = styled.div``;

const StateCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 8px;
  background-color: #eef1f1;
  width: 100%;
  height: 100%;
  text-align: center;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-bottom: 24px;
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
  .ant-card-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px;
    width: 100%;
    height: 100%;
  }
`;

const GradeCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 8px;
  background-color: #f5f5f5;
  width: 100%;
  height: 100%;
  text-align: center;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
  .ant-card-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 0px;
    height: 100%;
    width: 100%;
  }
`;

const ContainerWrapper = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  padding: 20px;
  background-color: #fff;
  margin-top: 20px;
`;

const StateCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StateCardTitle = styled.div`
  font-size: 19px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #252525;
  text-align: center;
`;

const StateCardSubtitle = styled.div`
  font-size: 12px;
  color: #252525;
`;

const ArrowIconWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  display: flex;
  align-self: end;
  padding: 10px;
`;

const ArrowIcon = styled.img`
  width: 28px;
`;

const ActionButton = styled(Button)`
  min-height: 38px;
`;

const OrangeButton = styled(Button)`
  background-color: #f27c22 !important;
  border-color: #ff9800 !important;
  color: #fff !important;
  width: 100%;
  padding: 10px 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: normal;
  line-height: 1.2;
  height: initial;
  min-width: 178px;
  gap: 1px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
`;

const TextDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: normal;
  line-height: 1.2;
  min-width: 178px;
  gap: 1px;
  font-size: 16px;
  font-weight: 600;
  color: #252525;
  padding: 10px 10px;
  background-color: #f5f5f5;
`;

const BackLink = styled.span`
  cursor: pointer;
  color: #252525;
  font-size: 18px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
`;

const BackLinkCol = styled(Col)`
  text-align: right;
`;

const GradeCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const GradeCardTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #252525;
  text-align: center;
  line-height: 28px;
`;

const GradeCardGrade = styled.div`
  font-size: 14px;
  color: #000;
  margin-bottom: 16px;
`;

const GradeCardActions = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-top: 50px;
`;

const ActionLink = styled.span`
  cursor: pointer;
  color: #1890ff;
  font-size: 14px;
  text-decoration: underline;
`;

const BreadcrumbText = styled.span`
  margin-left: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #666;
`;

const SearchInput = styled(Input)`
  height: 38px;
`;

const TableWrapper = styled(Table)`
  width: 100%;
  text-align: center;
  background-color: #fff;
  margin-top: 20px;
`;

const TableHeaderCell = styled(TableCell)`
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  padding: 16px;
`;

const TableCenterHeaderCell = styled(TableCell)`
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  padding: 16px;
  width: 100px;
`;

const TableBodyCell = styled(TableCell)`
  text-align: left;
  padding: 16px;
`;

const TableCenterBodyCell = styled(TableCell)`
  text-align: center;
  padding: 16px;
  cursor: pointer;
`;

const TableDescriptionCell = styled(TableCell)`
  text-align: left;
  padding: 16px;
  max-width: 400px;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableEmptyCell = styled(TableCell)`
  text-align: center;
  padding: 40px;
`;

const TableEmptyCellGray = styled(TableCell)`
  text-align: center;
  padding: 40px;
  color: #999;
`;

const ModalTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 40px;
`;

const GenerateButton = styled(Button)`
  min-width: 200px;
  min-height: 38px;
  background: ${(props) =>
    props.disabled ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: none;
  color: #ffffff;
  font-weight: 500;
`;

const TopRow = styled(Row)`
  margin-bottom: 20px;
`;

const ButtonRow = styled(Row)`
  margin-bottom: 16px;
`;

const StandardTable = ({ userloading }) => {
  const [filteredStanders, setFilteredStanders] = useState([]);
  const dispatch = useDispatch();
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = React.useState(0);
  const [standardModal, setStandardModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const standerds = useStandards();
  const enabledStateCodes = useOrgStandardCodes();
  const [deleteToggle, setDeleteToggle] = useState(false);
  const [approveToggle, setApproveToggle] = useState(false);
  const [loading, setLoader] = useState(false);
  const [viewMode, setViewMode] = useState('state');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [aiGenerateModalOpen, setAiGenerateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active');

  const hasViewStandard = useHasPermission('standard.view');
  const hasAddStandard = useHasPermission('standard.add');
  const hasEditStandard = useHasPermission('standard.edit');
  const hasDeleteStandard = useHasPermission('standard.delete');

  useEffect(() => {
    let filtered = [];
    
    if (viewMode === 'detail' && selectedState && selectedGrade) {
      const allGradeLevels = standerds
        .filter((std) => {
          const stdState = get(std, 'state');
          return statesMatch(stdState, selectedState.code);
        })
        .map(std => get(std, 'grade_level'))
        .filter(Boolean);
      
      filtered = filter(standerds, (std) => {
        const stdState = get(std, 'state');
        const stdGradeLevel = get(std, 'grade_level');
        const matchesState = statesMatch(stdState, selectedState.code);
        if (!matchesState) return false;
        
        const stdGroupingKey = getGradeGroupingKey(stdGradeLevel, allGradeLevels);
        const selectedGroupingKey = getGradeGroupingKey(selectedGrade, allGradeLevels);
        return stdGroupingKey === selectedGroupingKey;
      });
    } else {
      filtered = standerds;
    }
    
    if (viewMode === 'detail') {
      const statusFiltered = filter(filtered, (std) => {
        const status = get(std, 'status', true);
        if (activeTab === 'active') {
          return status === true || status === 1;
        } else {
          return status === false || status === 0;
        }
      });
      setFilteredStanders(statusFiltered);
    } else {
      setFilteredStanders(filtered);
    }
  }, [standerds, viewMode, selectedState, selectedGrade, activeTab]);

  const statesWithStandards = useMemo(() => {
    const states = [];
    STANDARD_GRADE_LEVEL.forEach((stateConfig) => {
      const hasStandards = standerds.some((std) => {
        const stdState = get(std, 'state');
        return statesMatch(stdState, stateConfig.code);
      });
      if (hasStandards) {
        states.push(stateConfig);
      }
    });
    return states;
  }, [standerds]);

  const gradesForState = useMemo(() => {
    if (!selectedState) return [];
    
    // First, collect all grade levels for this state
    const allGradeLevels = [];
    standerds.forEach((std) => {
      const stdState = get(std, 'state');
      let gradeLevel = null;
      if (statesMatch(stdState, selectedState.code)) {
        gradeLevel = get(std, 'grade_level');
      }
      if (gradeLevel) {
        allGradeLevels.push(gradeLevel);
      }
    });
    
    // Now group by grouping key (single grades grouped with their ranges)
    const gradeMap = {};
    allGradeLevels.forEach((gradeLevel) => {
      const groupingKey = getGradeGroupingKey(gradeLevel, allGradeLevels);
      if (!gradeMap[groupingKey]) {
        gradeMap[groupingKey] = {
          count: 0,
          displayGrade: groupingKey // Use the grouping key (which will be the range if it exists)
        };
      }
      gradeMap[groupingKey].count++;
    });
    
    // Return grouped grades sorted
    return Object.keys(gradeMap).sort((a, b) => {
      // Custom sort: Pre-K < K < numbers
      if (a === 'Pre-K') return -1;
      if (b === 'Pre-K') return 1;
      if (a === 'K') return b === 'Pre-K' ? 1 : -1;
      if (b === 'K') return 1;
      return a.localeCompare(b, undefined, { numeric: true });
    });
  }, [standerds, selectedState]);

  const handleStateClick = (state) => {
    setSelectedState(state);
    setViewMode('grade');
    setPage(0);
  };

  const handleGradeClick = (grade) => {
    setSelectedGrade(grade);
    setViewMode('detail');
    setPage(0);
  };

  const handleBackToStates = () => {
    setViewMode('state');
    setSelectedState(null);
    setSelectedGrade(null);
    setPage(0);
  };

  const handleBackToGrades = () => {
    setViewMode('grade');
    setSelectedGrade(null);
    setPage(0);
  };

  const onSearch = (event) => {
    const searchValue = event.target.value?.trim().toLowerCase();
    
    // Get all unique grade levels for grouping
    const allGradeLevels = selectedState ? standerds
      .filter((std) => {
        const stdState = get(std, 'state');
        return statesMatch(stdState, selectedState.code);
      })
      .map(std => get(std, 'grade_level'))
      .filter(Boolean) : [];
    
    if (searchValue && searchValue.length >= 1) {
      const baseStandards =
        viewMode === 'detail' && selectedState && selectedGrade
          ? filter(standerds, (std) => {
              const stdState = get(std, 'state');
              const stdGradeLevel = get(std, 'grade_level');
              const matchesState = statesMatch(stdState, selectedState.code);
              if (!matchesState) return false;
              
              const stdGroupingKey = getGradeGroupingKey(stdGradeLevel, allGradeLevels);
              const selectedGroupingKey = getGradeGroupingKey(selectedGrade, allGradeLevels);
              return stdGroupingKey === selectedGroupingKey;
            })
          : standerds;
      
      let filtered = filter(baseStandards, (x) => {
        return `${x.abbr || ''}`.toLowerCase().includes(searchValue);
      });
      
      if (viewMode === 'detail') {
        filtered = filter(filtered, (std) => {
          const status = get(std, 'status', true);
          if (activeTab === 'active') {
            return status === true || status === 1;
          } else {
            return status === false || status === 0;
          }
        });
      }
      
      setFilteredStanders(filtered);
    } else {
      if (viewMode === 'detail' && selectedState && selectedGrade) {
        let filtered = filter(standerds, (std) => {
          const stdState = get(std, 'state');
          const stdGradeLevel = get(std, 'grade_level');
          const matchesState = statesMatch(stdState, selectedState.code);
          if (!matchesState) return false;
          
          const stdGroupingKey = getGradeGroupingKey(stdGradeLevel, allGradeLevels);
          const selectedGroupingKey = getGradeGroupingKey(selectedGrade, allGradeLevels);
          return stdGroupingKey === selectedGroupingKey;
        });
        
        filtered = filter(filtered, (std) => {
          const status = get(std, 'status', true);
          if (activeTab === 'active') {
            return status === true || status === 1;
          } else {
            return status === false || status === 0;
          }
        });
        
        setFilteredStanders(filtered);
      } else {
        setFilteredStanders(standerds);
      }
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const onDeleteItem = async () => {
    if (!selectedItem) return;
    setLoader(true);
    await dispatch(handleDeleteStanders(selectedItem.id));
    setLoader(false);
    setDeleteToggle(false);
    setSelectedItem(null);
  };
  const handleAddStandard = () => {
    if (!hasAddStandard) {
      message.warning('You do not have permission to access this feature.');
      return;
    }
    setSelectedItem(null);
    setFormKey(prev => prev + 1); // Increment key to force form reset
    setStandardModal(true);
  };

  const handleEditStandard = (row) => {
    if (!hasEditStandard) {
      message.warning('You do not have permission to access this feature.');
      return;
    }
    setSelectedItem(row);
    setStandardModal(true);
  };

  const handleModalClose = () => {
    setStandardModal(false);
    setSelectedItem(null);
    setFormKey(prev => prev + 1); // Increment key to force form reset on next open
  };
  const handleDeleteStandard = (row) => {
    if (!hasDeleteStandard) {
      message.warning('You do not have permission to access this feature.');
      return;
    }
    setSelectedItem(row);
    setDeleteToggle(true);
  };

  const handleApproveStandardClick = (row) => {
    if (!hasEditStandard) {
      message.warning('You do not have permission to access this feature.');
      return;
    }
    setSelectedItem(row);
    setApproveToggle(true);
  };

  const onApproveItem = async () => {
    if (!selectedItem) return;
    setLoader(true);
    try {
      await dispatch(handleApproveStandard(selectedItem.id, true));
      message.success('Standard approved successfully');
      setApproveToggle(false);
      setSelectedItem(null);
    } catch (error) {
      message.error('Failed to approve standard');
    } finally {
      setLoader(false);
    }
  };
  const renderStateView = () => (
    <>
      <TopRow gutter={[16, 16]}>
        <Col xs={24} sm={12} md={16} style={{ display: 'flex', alignItems: 'center' }}>
          <ActionButton style={{ marginRight: '10px' }} onClick={handleAddStandard} type="primary" icon={<PlusOutlined />}>
            Add Standard
          </ActionButton>
          <GenerateButton 
            onClick={() => {
              if (!hasAddStandard) {
                message.warning('You do not have permission to access this feature.');
                return;
              }
              setAiGenerateModalOpen(true);
            }}
            type="primary"
            icon={<AutoAwesomeSharp />}
          >
            Add Standard with AI
          </GenerateButton>
        </Col>
      </TopRow>
      <ContainerWrapper>
        <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}>
          {statesWithStandards.map((state) => (
            <Col xs={24} sm={12} md={8} lg={6} key={state.code} style={{ display: 'flex' }}>
              <StateCard onClick={() => handleStateClick(state)}>
                <StateCardContent>
                  <StateCardTitle>
                    {state.state === 'National' ? (
                      <>
                        <div>Common Core</div>
                        <div>State Standards</div>
                      </>
                    ) : (
                        `${state.state} Learning Standards Spanish Language Arts`
                    )}
                  </StateCardTitle>
                </StateCardContent>
                <ArrowIconWrapper>
                  <ArrowIcon
                    src="https://imagedelivery.net/B62pqpMZqieDKhv80nP5iw/57ea6fa6-4e9f-4331-6212-3758173d0400/public"
                    alt="arrow down"
                  />
                </ArrowIconWrapper>
              </StateCard>
            </Col>
          ))}
        </Row>
      </ContainerWrapper>
    </>
  );

  const renderGradeView = () => (
    <>
      <TopRow gutter={[16, 16]}>
        <Col xs={24} sm={12} md={16} style={{ display: 'flex', alignItems: 'center' }}>
          <ActionButton style={{ marginRight: '10px' }} onClick={handleAddStandard} type="primary" icon={<PlusOutlined />}>
            Add Standard
          </ActionButton>
          <GenerateButton 
            onClick={() => {
              if (!hasAddStandard) {
                message.warning('You do not have permission to access this feature.');
                return;
              }
              setAiGenerateModalOpen(true);
            }}
            type="primary"
            icon={<AutoAwesomeSharp />}
          >
            Add Standard with AI
          </GenerateButton>
        </Col>
        <Col xs={24} sm={12} md={8} style={{ textAlign: 'end' }}>
          <Button icon={<ArrowBack />} onClick={handleBackToStates} style={{ marginBottom: 16 }}>
            Back
          </Button>
        </Col>
      </TopRow>
      <ContainerWrapper>
        <div style={{ display: 'inline-flex', gap: '8px', marginBottom: '35px', marginTop: '5px' }}>
          <TextDisplay>
            {selectedState?.state === 'National' ? (
              <>
                <div>Common Core</div>
                <div>State Standards</div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center' }}>{selectedState?.state}</div>
                <div style={{ textAlign: 'center' }}>{selectedState?.state} Learning Standards</div>
              </>
            )}
          </TextDisplay>
          <OrangeButton onClick={handleAddStandard}>
            <div>Add Grade</div>
            <div>Level Standards</div>
          </OrangeButton>
        </div>
        <Row gutter={[16, 16]} className='setrowspace' style={{ display: 'flex', flexWrap: 'wrap' }}>
            {gradesForState.map((grade) => {
              const gradeDisplay =
                grade === 'Pre-K'
                  ? 'Pre-K'
                  : grade === 'K'
                    ? 'K'
                    : grade.includes('-') || grade.includes('to')
                      ? grade.replace('-', '/').replace('to', '/')
                      : `${grade}th grade`;
              return (
                <Col xs={24} sm={12} md={8} lg={4} key={grade} className='setDekstopwidth' style={{ display: 'flex' }}>
                  <GradeCard>
                    <GradeCardContent>
                      <GradeCardTitle>
                        {selectedState.state === 'National' ? (
                          <>
                            <div>Common Core</div>
                            <div>State Standards</div>
                            <div>{gradeDisplay}</div>
                          </>
                        ) : (
                          <>
                            <div>{selectedState.state}</div>
                            <div>Learning Standards</div>
                            <div>{gradeDisplay}</div>
                          </>
                        )}
                      </GradeCardTitle>
                    </GradeCardContent>
                    <GradeCardActions>
                      <GradeCardTitle onClick={() => handleGradeClick(grade)}>See</GradeCardTitle>
                      <GradeCardTitle
                        onClick={() => {
                          // Get all standards that match this grade (including variations)
                          const standardsForGrade = standerds.filter((std) => {
                            const stdState = get(std, 'state');
                            const stdGradeLevel = get(std, 'grade_level');
                            const matchesState = statesMatch(stdState, selectedState.code);
                            if (!matchesState) return false;
                            
                            // Compare using grouping key - if grade is "9-10", it should match both "9" and "9-10"
                            const stdGroupingKey = getGradeGroupingKey(stdGradeLevel, gradesForState);
                            return stdGroupingKey === grade;
                          });
                          if (standardsForGrade.length > 0) {
                            handleEditStandard(standardsForGrade[0]);
                          }
                        }}
                      >
                        Edit
                      </GradeCardTitle>
                    </GradeCardActions>
                  </GradeCard>
                </Col>
              );
            })}
        </Row>
      </ContainerWrapper>
    </>
  );

  const renderDetailView = () => (
    <>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <BreadcrumbText>
            {selectedState?.state === 'National'
              ? 'Common Core State Standards'
              : `${selectedState?.state} Learning Standards`}{' '}
            -{' '}
            {selectedGrade === 'Pre-K'
              ? 'Pre-K'
              : selectedGrade === 'K'
                ? 'K'
                : selectedGrade.includes('-') || selectedGrade.includes('to')
                  ? selectedGrade
                  : `${selectedGrade}th grade`}
          </BreadcrumbText>
        </Col>
      </Row>
      <TopRow gutter={[16, 16]}>
        <Col xs={24} sm={12} md={16} style={{ display: 'flex', alignItems: 'center' }}>
          <ActionButton style={{ marginRight: '10px' }} onClick={handleAddStandard} type="primary" icon={<PlusOutlined />}>
            Add Standard
          </ActionButton>
          <GenerateButton 
            onClick={() => {
              if (!hasAddStandard) {
                message.warning('You do not have permission to access this feature.');
                return;
              }
              setAiGenerateModalOpen(true);
            }}
            type="primary"
            icon={<AutoAwesomeSharp />}
          >
            Add Standard with AI
          </GenerateButton>
        </Col>
        <Col xs={24} sm={12} md={8} style={{ textAlign: 'end' }}>
          <Button icon={<ArrowBack />} onClick={handleBackToGrades} style={{ marginBottom: 16 }}>
            Back
          </Button>
        </Col>
        <Col xs={24} sm={24} md={24}>
          <SearchInput
            placeholder="Search by abbreviation"
            variant="outlined"
            allowClear
            onChange={onSearch}
          />
        </Col>
      </TopRow>
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'active',
                label: 'Active',
              },
              {
                key: 'draft',
                label: 'Draft',
              },
            ]}
          />
        </Col>
      </Row>
      {hasViewStandard && (
        <StableWrapper>
          <TableWrapper stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Abbreviation</TableHeaderCell>
                <TableHeaderCell>Grade Level</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableCenterHeaderCell>Edit</TableCenterHeaderCell>
                {activeTab === 'draft' && <TableCenterHeaderCell>Approve</TableCenterHeaderCell>}
                <TableCenterHeaderCell>Delete</TableCenterHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isEmpty(filteredStanders) &&
                (userloading ? (
                  <TableRow>
                    <TableEmptyCell colSpan={activeTab === 'draft' ? 6 : 5}>
                      <Loading size={30} />
                    </TableEmptyCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableEmptyCellGray colSpan={activeTab === 'draft' ? 6 : 5}>No results found</TableEmptyCellGray>
                  </TableRow>
                ))}
              {!isEmpty(filteredStanders) &&
                filteredStanders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow key={row.id} hover>
                        <TableBodyCell>
                          {getStandardAbbr(
                            row,
                            selectedState ? [selectedState.code] : enabledStateCodes,
                          ) || get(row, 'abbr', '-')}
                        </TableBodyCell>
                        <TableBodyCell>
                          {get(row, 'grade_level', '-')}
                        </TableBodyCell>
                        <TableDescriptionCell
                          title={
                            getStandardDescription(
                              row,
                              selectedState ? [selectedState.code] : enabledStateCodes,
                            ) || '-'
                          }
                        >
                          {getStandardDescription(
                            row,
                            selectedState ? [selectedState.code] : enabledStateCodes,
                          ) || '-'}
                        </TableDescriptionCell>
                        <TableCenterBodyCell onClick={() => handleEditStandard(row)}>
                          <Edit style={{ color: '#1890ff' }} />
                        </TableCenterBodyCell>
                        {activeTab === 'draft' && (
                          <TableCenterBodyCell style={{ color: 'red' }} onClick={() => handleApproveStandardClick(row)}>
                            <Done />
                          </TableCenterBodyCell>
                        )}
                        <TableCenterBodyCell onClick={() => handleDeleteStandard(row)}>
                          <Delete style={{ color: '#ff4d4f' }} />
                        </TableCenterBodyCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
            <TableFooter>
              <TableRow>
                {!isEmpty(filteredStanders) && (
                  <TablePagination
                    count={filteredStanders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    slotProps={{
                      actions: {
                        previousButton: {
                          color: 'secondary',
                        },
                        nextButton: {
                          color: 'secondary',
                        },
                      },
                    }}
                  />
                )}
              </TableRow>
            </TableFooter>
          </TableWrapper>
        </StableWrapper>
      )}
    </>
  );

  return (
    <>
      <StableWrapperMain>
        {viewMode === 'state' && renderStateView()}
        {viewMode === 'grade' && renderGradeView()}
        {viewMode === 'detail' && renderDetailView()}
      </StableWrapperMain>
      <Modal
        footer={null}
        title={
          <ModalTitleWrapper>
            <span>{selectedItem ? 'Edit Standard' : 'Add Standard'}</span>
          </ModalTitleWrapper>
        }
        centered
        open={standardModal}
        onCancel={handleModalClose}
        width={'80%'}
        className="FilterHeading"
        styles={{
          body: {
            height: 'calc(85vh - 55px)',
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <StandardForm
          key={`${selectedItem?.id || 'new'}-${formKey}`}
          standers={selectedItem}
          onChangeStanders={handleModalClose}
          defaultState={selectedState}
          defaultGrade={selectedGrade}
        />
      </Modal>
      <DeleteModal
        deleteToggle={deleteToggle}
        setDeleteToggle={setDeleteToggle}
        onDelete={onDeleteItem}
        message={`Are you sure you want to delete this standard?`}
        loading={loading}
      />
      <DeleteModal
        deleteToggle={approveToggle}
        setDeleteToggle={setApproveToggle}
        onDelete={onApproveItem}
        message={`Are you sure you want to approve this standard?`}
        loading={loading}
        buttonTitle="Approve"
        colorSchema="primary"
      />
      <AiGenerateStandardsModal
        open={aiGenerateModalOpen}
        onClose={() => setAiGenerateModalOpen(false)}
        onSuccess={() => {
          setAiGenerateModalOpen(false);
        }}
      />
    </>
  );
};

export default StandardTable;
